import { formatDistanceToNow } from 'date-fns';
import { match } from 'ts-pattern';

import {
  MAXIMUM_GAS_ACCOUNT_BALANCE,
  MINIMUM_GAS_ACCOUNT_TOPUP,
} from '@infinex/main-service-contract/v2/client/gas';
import { FeeAccountIcon, StarShineIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import {
  Button,
  Content,
  Emblem,
  Skeleton,
  SkeletonText,
  SurfaceStack,
  SurfaceStackSegment,
  useDialogRouteState,
  useDialogState,
  XStack,
  YStack,
} from '@infinex/ui/components';

import { LegalInfo } from './LegalInfo';

import { useAssetMetadataQuery } from '../../../api/assets/assetQueries';
import type { GasActivity } from '../../../types/activity';
import { getActivityAssetGroupId } from '../../activity-log';
import { FormattedPrice } from '../../formatted-number';
import { useGasDialogConfig } from '../GasDialogDependenciesContext';
import type { GasInfo } from '../types';

const Activity = (activity: GasActivity) => {
  const { action, formattedGasAmount, timestamp } = activity;
  const groupId = getActivityAssetGroupId(activity);
  const { data: assetMetadata } = useAssetMetadataQuery(groupId, {
    enabled: !!groupId, // Keep this check to prevent queries for null values
  });
  return (
    <XStack align="center" justify="space-between" paddingY="12px" gap="12px">
      <XStack gap="8px" align="center">
        <Emblem
          activity={'gas'}
          src={assetMetadata?.imgUrl}
          alt={assetMetadata?.symbol ?? 'unknown symbol'}
          asset={assetMetadata?.type}
          size={'medium'}
        />
        <YStack>
          <span className="text-secondary text-sm font-semibold whitespace-nowrap">
            {match(action)
              .with('SPENT', () => 'Spent from Gas Account')
              .with('JIT_TOP_UP', () => 'Topped up Gas Account')
              .with('PREPAID_TOP_UP', () => 'Topped up Gas Account')
              .exhaustive()}
          </span>
          <span className="text-base font-semibold">
            <FormattedPrice
              value={Number(formattedGasAmount)}
              prefixDollarSign={true}
            />
          </span>
        </YStack>
      </XStack>

      <XStack className="text-secondary text-right text-xs" justify="center">
        {formatDistanceToNow(new Date(timestamp), {
          addSuffix: true,
        })}
      </XStack>
    </XStack>
  );
};

function GasBalance({
  isLoading,
  gasBalance,
}: Pick<GasInfo, 'isLoading' | 'gasBalance'>) {
  const balance = gasBalance ?? 0;
  return (
    <YStack align="center">
      <FeeAccountIcon fill="default" className="mb-2.5" />

      {isLoading ? (
        <Skeleton className="flex w-full justify-center">
          <SkeletonText size="4xl" width={0.4} />
        </Skeleton>
      ) : (
        <h2
          className={cn(
            'text-center text-4xl font-bold',
            balance > 0 ? 'text-emphasis' : 'text-tertiary'
          )}
        >
          <FormattedPrice value={balance} prefixDollarSign={true} />
        </h2>
      )}

      <h5 className="text-secondary mt-1 text-center text-sm font-semibold">
        Credit available
      </h5>
    </YStack>
  );
}

function AddGasSection({ isLoading, gasBalance }: GasInfo) {
  const dialogRouteState = useDialogRouteState();
  const balance = gasBalance ?? 0;

  if (balance > MAXIMUM_GAS_ACCOUNT_BALANCE - MINIMUM_GAS_ACCOUNT_TOPUP) {
    return (
      <YStack align="center">
        <span className="text-secondary text-sm font-medium">Well done!</span>
        <span className="text-secondary text-sm font-medium">
          You've reached the maximum balance.
        </span>
      </YStack>
    );
  }

  return (
    <Button
      prominence="high"
      onPress={() => dialogRouteState?.push('add-gas')}
      isDisabled={isLoading}
    >
      Top up credit
    </Button>
  );
}

export const HomeScreen = (props: GasInfo) => {
  const { gasBalance, activities, isLoading } = props;
  const dialogState = useDialogState();
  const { onSeeAllActivity } = useGasDialogConfig();

  const hasPreviousActivity = activities.length > 0;

  const handleSeeAll = () => {
    dialogState.close();
    onSeeAllActivity?.();
  };

  if (hasPreviousActivity) {
    return (
      <Content>
        <YStack gap="32px">
          <SurfaceStack>
            <SurfaceStackSegment className="py-8">
              <GasBalance isLoading={isLoading} gasBalance={gasBalance} />
            </SurfaceStackSegment>

            <SurfaceStackSegment className="grid content-normal">
              <AddGasSection {...props} />
            </SurfaceStackSegment>
          </SurfaceStack>

          <div className="flex-col-fill gap-2">
            <div className="flex items-center justify-between">
              <h5 className="text-emphasis font-semibold">Recent activity</h5>

              {onSeeAllActivity ? (
                <Button
                  prominence="low"
                  size="small"
                  onPress={handleSeeAll}
                  className="text-default"
                >
                  See all
                </Button>
              ) : null}
            </div>

            {activities.map((activity) => (
              <Activity key={activity.id} {...activity} />
            ))}
          </div>

          <LegalInfo />
        </YStack>
      </Content>
    );
  }

  return (
    <Content>
      <YStack gap="32px">
        <SurfaceStack>
          <SurfaceStackSegment className="py-8">
            <GasBalance isLoading={isLoading} gasBalance={gasBalance} />
          </SurfaceStackSegment>
        </SurfaceStack>

        {/*
         * TODO @infinex/ui:
         * This should probably be an <EmptyState /> or <EmptySection /> component but a mix between the two
         */}
        <div className="border-tertiary rounded-medium flex flex-col items-center gap-y-4 border-2 border-dashed p-8">
          <div className="flex flex-col items-center justify-center gap-y-0.5 text-center">
            <StarShineIcon fill="brand" size="xsmall" className="self-center" />

            <h2 className="text-emphasis text-lg font-semibold">
              Top up to get started
            </h2>

            <p className="text-secondary text-sm">
              Network costs are paid from this account.
            </p>
          </div>

          <AddGasSection {...props} />
        </div>

        <LegalInfo />
      </YStack>
    </Content>
  );
};
